function vpFormatMoney(cents, currency) {
  const amount = typeof cents === 'number' ? cents : parseInt(cents, 10) || 0;

  if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
    const format = window.theme?.moneyFormat || window.Shopify?.money_format;
    return format ? Shopify.formatMoney(amount, format) : Shopify.formatMoney(amount);
  }

  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency || window.Shopify?.currency?.active || 'USD',
    }).format(amount / 100);
  } catch {
    return `$${(amount / 100).toFixed(2)}`;
  }
}

function vpEscapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function vpGetImageCenterColor(img) {
  if (!img?.naturalWidth || !img.naturalHeight) return null;

  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 1;
    canvas.height = 1;
    context.drawImage(
      img,
      Math.floor(img.naturalWidth / 2),
      Math.floor(img.naturalHeight / 2),
      1,
      1,
      0,
      0,
      1,
      1
    );
    const [r, g, b] = context.getImageData(0, 0, 1, 1).data;
    return `rgb(${r}, ${g}, ${b})`;
  } catch {
    return null;
  }
}

function vpApplySwatchRingColors(root) {
  root.querySelectorAll('.vp-cart-drawer__swatch').forEach((swatch) => {
    if (swatch.style.getPropertyValue('--swatch-ring-color')) return;

    const img = swatch.querySelector('.vp-cart-drawer__swatch-image');
    if (!img) return;

    const applyColor = () => {
      const color = vpGetImageCenterColor(img);
      if (color) swatch.style.setProperty('--swatch-ring-color', color);
    };

    if (img.complete) {
      applyColor();
    } else {
      img.addEventListener('load', applyColor, { once: true });
    }
  });
}

const UpsellEngine = {
  getConfig() {
    return window.cartUpsellConfig || { groups: [] };
  },

  getEnabledGroups() {
    return (this.getConfig().groups || []).filter((group) => group.enabled !== false && group.products?.length);
  },

  findGroupForProduct({ productId, handle } = {}) {
    const id = productId != null ? String(productId) : null;

    return (
      this.getEnabledGroups().find((group) =>
        group.products.some(
          (product) => (id && String(product.id) === id) || (handle && product.handle === handle)
        )
      ) || null
    );
  },

  getCartItemGroup(item) {
    return this.findGroupForProduct({
      productId: item.product_id,
      handle: item.handle,
    });
  },

  summarizeCart(cart) {
    const summary = {};

    this.getEnabledGroups().forEach((group) => {
      summary[group.id] = {
        group,
        quantity: 0,
        productIds: new Set(),
        colours: new Set(),
      };
    });

    (cart?.items || []).forEach((item) => {
      const group = this.getCartItemGroup(item);
      if (!group) return;

      const entry = summary[group.id];
      entry.quantity += item.quantity;
      entry.productIds.add(String(item.product_id));

      const configProduct = group.products.find(
        (product) => String(product.id) === String(item.product_id) || product.handle === item.handle
      );

      if (configProduct?.colour) {
        entry.colours.add(configProduct.colour);
      }
    });

    return summary;
  },

  getVisibleGroups(cart) {
    if (!cart?.item_count) return [];

    return this.getEnabledGroups();
  },

  getDefaultColourProduct(group, cart) {
    if (!group?.products?.length) return null;

    const summary = this.summarizeCart(cart)[group.id];
    const cartProductIds = summary?.productIds || new Set();
    const cartColours = summary?.colours || new Set();

    const differentColour = group.products.find(
      (product) => !cartProductIds.has(String(product.id)) && !cartColours.has(product.colour)
    );

    if (differentColour) return differentColour;

    return group.products.find((product) => !cartProductIds.has(String(product.id))) || group.products[0];
  },

  getLastAddedItem(cart, lastAddedItem) {
    if (lastAddedItem) {
      const stillInCart = (cart?.items || []).some(
        (item) =>
          item.key === lastAddedItem.key ||
          String(item.variant_id) === String(lastAddedItem.variant_id)
      );

      if (stillInCart) return lastAddedItem;
    }

    // Cart.js returns items newest-first; items[0] is the last added.
    const items = cart?.items || [];
    return items.length ? items[0] : null;
  },

  getPrefillSelection(cart, lastAddedItem) {
    return VariantSelector.selectionFromCartItem(this.getLastAddedItem(cart, lastAddedItem));
  },

  getHeadline(cart) {
    return (cart?.item_count || 0) === 1
      ? 'Add a 2nd pair, take 25% off both'
      : 'Add another pair, still 25% off';
  },

  getDisplayPriceCents(product, cart) {
    const base = product?.price || product?.variants?.[0]?.price || 0;
    if ((cart?.item_count || 0) >= 1) {
      return Math.round(base * 0.75);
    }
    return base;
  },
};

class VpCartUpsell {
  constructor(cartDrawer) {
    this.cartDrawer = cartDrawer;
    this.selectedByGroup = {};
    // Tracks which colour products are in the cart per group, so we can
    // re-default the swatch when cart membership changes (e.g. remove Brown).
    this.cartSignatureByGroup = {};
    this.openGroupId = null;
    this.pick = { size: null, inseam: null };
    this.productByHandle = {};
    this.loading = false;
    this.error = '';
  }

  get root() {
    return this.cartDrawer.querySelector('[data-vp-cart-upsell]');
  }

  get cart() {
    return this.cartDrawer.state?.cart;
  }

  cartSignatureForGroup(group) {
    const summary = UpsellEngine.summarizeCart(this.cart)[group.id];
    return [...(summary?.productIds || [])].sort().join('|');
  }

  getSelectedProduct(group) {
    const selectedId = this.selectedByGroup[group.id];
    if (selectedId) {
      const match = group.products.find((product) => String(product.id) === String(selectedId));
      if (match) return match;
    }

    const fallback = UpsellEngine.getDefaultColourProduct(group, this.cart);
    if (fallback) this.selectedByGroup[group.id] = fallback.id;
    return fallback;
  }

  ensureSelectionDefaults(groups) {
    groups.forEach((group) => {
      const signature = this.cartSignatureForGroup(group);
      const cartChanged = this.cartSignatureByGroup[group.id] !== signature;
      this.cartSignatureByGroup[group.id] = signature;

      const selectedId = this.selectedByGroup[group.id];
      const stillInGroup =
        selectedId &&
        group.products.some((product) => String(product.id) === String(selectedId));

      // Re-pick opposite colour whenever cart colours for this style change,
      // otherwise a sticky Black selection survives after Brown is removed.
      if (!stillInGroup || cartChanged) {
        const product = UpsellEngine.getDefaultColourProduct(group, this.cart);
        this.selectedByGroup[group.id] = product?.id || null;
      }
    });
  }

  async loadProduct(handle) {
    if (!handle) return null;
    if (this.productByHandle[handle]) return this.productByHandle[handle];

    const cached = ProductData.getCached(handle);
    if (cached) {
      this.productByHandle[handle] = cached;
      return cached;
    }

    const product = await ProductData.get(handle);
    this.productByHandle[handle] = product;
    return product;
  }

  applyPrefill(product) {
    const prefill = UpsellEngine.getPrefillSelection(this.cart, this.cartDrawer.state?.lastAddedItem);
    let size = prefill[VP_OPTION_KEYS.SIZE];
    let inseam = prefill[VP_OPTION_KEYS.INSEAM];

    if (size && VariantSelector.getOptionState(product, {}, VP_OPTION_KEYS.SIZE, size) !== 'available') {
      size = null;
    }

    if (
      inseam &&
      VariantSelector.getOptionState(product, { size }, VP_OPTION_KEYS.INSEAM, inseam) !== 'available'
    ) {
      inseam = null;
    }

    this.pick = { size, inseam };
  }

  async openPicker(groupId) {
    const groups = UpsellEngine.getVisibleGroups(this.cart);
    const group = groups.find((entry) => entry.id === groupId);
    if (!group) return;

    const configProduct = this.getSelectedProduct(group);
    if (!configProduct?.handle) return;

    this.loading = true;
    this.error = '';
    this.openGroupId = groupId;
    this.render();

    try {
      const product = await this.loadProduct(configProduct.handle);
      this.applyPrefill(product);
    } catch (error) {
      console.error('[vp-cart-upsell] Failed to load product', error);
      this.error = 'Unable to load sizes. Try again.';
      this.openGroupId = null;
    } finally {
      this.loading = false;
      this.render();
    }
  }

  closePicker() {
    this.openGroupId = null;
    this.pick = { size: null, inseam: null };
    this.error = '';
    this.render();
  }

  async selectColour(groupId, productId) {
    this.selectedByGroup[groupId] = productId;
    this.error = '';

    if (this.openGroupId === groupId) {
      const group = UpsellEngine.getVisibleGroups(this.cart).find((entry) => entry.id === groupId);
      const configProduct = group?.products.find((product) => String(product.id) === String(productId));

      if (configProduct?.handle) {
        this.loading = true;
        this.render();

        try {
          const product = await this.loadProduct(configProduct.handle);
          this.applyPrefill(product);
        } catch (error) {
          console.error('[vp-cart-upsell] Failed to load colour product', error);
          this.error = 'Unable to load sizes. Try again.';
        } finally {
          this.loading = false;
        }
      }
    }

    this.render();
  }

  onSelectChange(kind, value) {
    this.pick = {
      ...this.pick,
      [kind]: value || null,
    };

    if (kind === VP_OPTION_KEYS.SIZE) {
      const group = UpsellEngine.getVisibleGroups(this.cart).find((entry) => entry.id === this.openGroupId);
      const configProduct = group ? this.getSelectedProduct(group) : null;
      const product = configProduct ? this.productByHandle[configProduct.handle] : null;

      if (
        product &&
        this.pick.inseam &&
        VariantSelector.getOptionState(product, this.pick, VP_OPTION_KEYS.INSEAM, this.pick.inseam) !==
          'available'
      ) {
        this.pick.inseam = null;
      }
    }

    this.render();
  }

  async confirmAdd(groupId) {
    const group = UpsellEngine.getVisibleGroups(this.cart).find((entry) => entry.id === groupId);
    const configProduct = group ? this.getSelectedProduct(group) : null;
    const product = configProduct ? this.productByHandle[configProduct.handle] : null;

    if (!product) return;

    const variant = VariantSelector.findMatchingVariant(product, this.pick);
    if (!variant?.id || variant.available === false) {
      this.error = 'Select an available size and inseam.';
      this.render();
      return;
    }

    this.loading = true;
    this.error = '';
    this.render();

    try {
      const properties = configProduct.colour ? { _colour: configProduct.colour } : null;
      await this.cartDrawer.addUpsellVariant(variant.id, properties);
      this.openGroupId = null;
      this.pick = { size: null, inseam: null };
    } catch (error) {
      console.error('[vp-cart-upsell] Failed to add upsell', error);
      this.error = 'Could not add to bag. Try again.';
    } finally {
      this.loading = false;
      this.render();
    }
  }

  bindEvents(root) {
    root.querySelectorAll('[data-vp-swatch]').forEach((button) => {
      button.addEventListener('click', () => {
        this.selectColour(button.dataset.groupId, button.dataset.productId);
      });
    });

    root.querySelectorAll('[data-vp-upsell-open]').forEach((button) => {
      button.addEventListener('click', () => {
        this.openPicker(button.dataset.groupId);
      });
    });

    root.querySelectorAll('[data-vp-upsell-cancel]').forEach((button) => {
      button.addEventListener('click', () => this.closePicker());
    });

    root.querySelectorAll('[data-vp-upsell-size]').forEach((select) => {
      select.addEventListener('change', (event) => {
        this.onSelectChange(VP_OPTION_KEYS.SIZE, event.target.value);
      });
    });

    root.querySelectorAll('[data-vp-upsell-inseam]').forEach((select) => {
      select.addEventListener('change', (event) => {
        this.onSelectChange(VP_OPTION_KEYS.INSEAM, event.target.value);
      });
    });

    root.querySelectorAll('[data-vp-upsell-confirm]').forEach((button) => {
      button.addEventListener('click', () => {
        this.confirmAdd(button.dataset.groupId);
      });
    });
  }

  renderSelectOptions(selectData, kind) {
    const data = selectData?.[kind];
    if (!data) return `<option value="">${kind === 'size' ? 'Size' : 'Inseam'}</option>`;

    const placeholder = kind === VP_OPTION_KEYS.SIZE ? 'Size' : 'Inseam';
    const selected = this.pick[kind];

    return (
      `<option value="">${placeholder}</option>` +
      data.options
        .map((option) => {
          const isSelected = selected === option.value;
          return `<option value="${vpEscapeHtml(option.value)}"${option.disabled ? ' disabled' : ''}${
            isSelected ? ' selected' : ''
          }>${vpEscapeHtml(option.label)}</option>`;
        })
        .join('')
    );
  }

  renderGroupRow(group) {
    const configProduct = this.getSelectedProduct(group);
    if (!configProduct) return '';

    if (configProduct.handle && !this.productByHandle[configProduct.handle]) {
      const cached = ProductData.getCached(configProduct.handle);
      if (cached) this.productByHandle[configProduct.handle] = cached;
    }

    const product = this.productByHandle[configProduct.handle];
    const priceCents = product
      ? UpsellEngine.getDisplayPriceCents(product, this.cart)
      : null;
    const priceLabel = priceCents != null ? vpFormatMoney(priceCents) : '';
    const isOpen = this.openGroupId === group.id;
    const selectData = product ? VariantSelector.buildSelectData(product, this.pick) : null;
    const ready = Boolean(this.pick.size && this.pick.inseam && !this.loading);

    const swatches = group.products
      .map((entry) => {
        const on = String(entry.id) === String(configProduct.id);
        const label = entry.colour || entry.title || '';
        const ringStyle = entry.swatch_color
          ? ` style="--swatch-ring-color: ${vpEscapeHtml(entry.swatch_color)};"`
          : '';

        let inner = '';
        if (entry.swatch_image && !entry.is_bette) {
          inner = `<img class="vp-cart-drawer__swatch-image" src="${vpEscapeHtml(
            entry.swatch_image
          )}" alt="" width="16" height="16" loading="lazy" crossorigin="anonymous">`;
        } else {
          const fallbackColor = entry.swatch_color || '#cccccc';
          inner = `<span class="vp-cart-drawer__swatch-fallback" style="background: ${vpEscapeHtml(
            fallbackColor
          )}" aria-hidden="true"></span>`;
        }

        return `<button type="button" class="vp-cart-drawer__swatch${on ? ' is-on' : ''}" data-vp-swatch data-group-id="${vpEscapeHtml(
          group.id
        )}" data-product-id="${vpEscapeHtml(entry.id)}"${ringStyle} aria-label="${vpEscapeHtml(label)}"${
          on ? ' aria-pressed="true"' : ' aria-pressed="false"'
        }><span class="vp-cart-drawer__swatch-inner">${inner}</span></button>`;
      })
      .join('');

    const thumb = configProduct.featured_image
      ? `<img src="${vpEscapeHtml(configProduct.featured_image)}" alt="" width="46" height="61" loading="lazy">`
      : '';

    const meta = `${vpEscapeHtml(configProduct.colour || '')}${priceLabel ? ` · <b>${vpEscapeHtml(priceLabel)}</b>` : ''}`;

    let actions = isOpen
      ? `<button type="button" class="vp-cart-drawer__addb" data-vp-upsell-cancel>Cancel</button>`
      : `<button type="button" class="vp-cart-drawer__addb" data-vp-upsell-open data-group-id="${vpEscapeHtml(
          group.id
        )}"${this.loading ? ' disabled' : ''}>Add</button>`;

    let picker = '';
    if (isOpen) {
      picker = `<div class="vp-cart-drawer__sel">
        <select data-vp-upsell-size aria-label="Size"${this.loading ? ' disabled' : ''}>
          ${this.renderSelectOptions(selectData, VP_OPTION_KEYS.SIZE)}
        </select>
        <select data-vp-upsell-inseam aria-label="Inseam"${this.loading ? ' disabled' : ''}>
          ${this.renderSelectOptions(selectData, VP_OPTION_KEYS.INSEAM)}
        </select>
        <button type="button" class="vp-cart-drawer__sel-ok" data-vp-upsell-confirm data-group-id="${vpEscapeHtml(
          group.id
        )}"${!ready ? ' disabled' : ''}>Add</button>
      </div>`;
    }

    return `<div class="vp-cart-drawer__upc" data-group-id="${vpEscapeHtml(group.id)}">
      <div class="vp-cart-drawer__upc-main">
        <div class="vp-cart-drawer__upc-thumb">${thumb}</div>
        <div class="vp-cart-drawer__upc-info">
          <div class="vp-cart-drawer__upc-name">${vpEscapeHtml(group.name)}</div>
          <div class="vp-cart-drawer__upc-meta">${meta}</div>
          <div class="vp-cart-drawer__swatches">${swatches}</div>
        </div>
        ${actions}
      </div>
      ${picker}
    </div>`;
  }

  render() {
    const root = this.root;
    if (!root) return;

    const groups = UpsellEngine.getVisibleGroups(this.cart);

    if (!groups.length) {
      root.hidden = true;
      root.innerHTML = '';
      return;
    }

    this.ensureSelectionDefaults(groups);
    root.hidden = false;

    const headline = UpsellEngine.getHeadline(this.cart);
    const rows = groups.map((group) => this.renderGroupRow(group)).join('');
    const error = this.error
      ? `<p class="vp-cart-drawer__upsell-error" role="alert">${vpEscapeHtml(this.error)}</p>`
      : '';

    root.innerHTML = `<div class="vp-cart-drawer__up">
      <div class="vp-cart-drawer__up-label">${vpEscapeHtml(headline)}</div>
      ${rows}
      ${error}
    </div>`;

    this.bindEvents(root);
    vpApplySwatchRingColors(root);
  }
}

window.VPUpsellEngine = UpsellEngine;
window.VpCartUpsell = VpCartUpsell;
